import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  fetchLeadAssignments, saveServiceRoster, removeFromService, clearServiceRoster,
  fetchAllEmployees,
} from "../services/leadAssignmentService";

const KEY = "lead-assignments";

/* No stale window: a roster change decides where live leads land, so the screen
   must never show yesterday's mapping. */
export const useLeadAssignments = (filters = {}) =>
  useQuery({
    queryKey: [KEY, filters],
    queryFn: () => fetchLeadAssignments(filters),
    staleTime: 0,
    refetchOnMount: "always",
  });

export const useAllEmployees = () =>
  useQuery({
    queryKey: ["all-employees"],
    queryFn: fetchAllEmployees,
    staleTime: 0,
    refetchOnMount: "always",
  });

/** Shared plumbing: every write refreshes the whole board. */
const useAssignmentMutation = (mutationFn, successMessage) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      if (successMessage) toast.success(successMessage);
      queryClient.invalidateQueries({ queryKey: [KEY], refetchType: "all" });
    },
    // The service layer has already turned 403 / 422 into a readable line.
    onError: (err) => toast.error(err?.message || "Something went wrong"),
  });
};

export const useSaveRoster = () =>
  useAssignmentMutation(
    ({ serviceId, employees }) => saveServiceRoster(serviceId, employees),
    "Lead assignment saved",
  );

export const useRemoveFromService = () =>
  useAssignmentMutation(({ serviceId, userId }) => removeFromService(serviceId, userId), null);

export const useClearService = () =>
  useAssignmentMutation(({ serviceId }) => clearServiceRoster(serviceId), null);
